/**
 * Registro dei prodotti della piattaforma (vedi docs/ARCHITETTURA-PRODOTTI-E-DOMINI.md).
 *
 * Prodotto = superficie vendibile con un proprio dominio (Console Core, Construction, …).
 * Modulo di feature = capacità senza dominio (crm, billing, …) ospitata da un prodotto.
 *
 * Questo file è puro (nessun import di React, router o import.meta): lo usano l'app,
 * gli script e i test. Gli host per ambiente stanno in ./hosts.ts.
 */

export type ProductKey = "console" | "construction" | "supply" | "crm" | "billing";

export type ProductKind = "console" | "sector" | "standalone";

/** Moduli di feature: capacità ospitabili da più prodotti. */
export type FeatureModuleKey = "crm" | "billing" | "supply-buyer";

export interface EmbeddedModule {
  module: FeatureModuleKey;
  /** Chiavi di tenant_modules che abilitano il modulo (alias inclusi, vedi module-key-aliases.ts) */
  entitlementKeys: readonly string[];
}

export interface ProductDefinition {
  key: ProductKey;
  kind: ProductKind;
  /** Nome commerciale mostrato all'utente */
  name: string;
  /**
   * Prefissi di percorso posseduti dal prodotto. Un percorso appartiene a un solo prodotto.
   * Finché gli URL hanno ancora il prefisso (es. /construction/…) è questo che decide
   * a quale prodotto appartiene una pagina.
   */
  pathPrefixes: readonly string[];
  /** Pagina di accesso */
  loginPath: string;
  /** Prefisso usato dal client Supabase per isolare il token di sessione nel localStorage */
  sessionKey: string;
  /** Modulo di settore (solo kind = "sector") — chiave in modules_registry */
  sectorModule?: string;
  /** Moduli di feature ospitati dal prodotto, che ne prendono le sembianze */
  embeds: readonly EmbeddedModule[];
  /** Per i prodotti standalone: il modulo di feature che vendono da soli */
  standaloneModule?: FeatureModuleKey;
  /** App mobile dedicata (Capacitor), se esiste */
  mobileAppId?: string;
}

const CRM_KEYS = ["crm", "companies", "contacts", "pipelines", "activities"] as const;
const BILLING_KEYS = ["billing", "fatturazione"] as const;

export const PRODUCTS: Record<ProductKey, ProductDefinition> = {
  console: {
    key: "console",
    kind: "console",
    name: "Mindlead Suite",
    pathPrefixes: [
      "/core",
      "/platform-admin",
      "/settings",
      // pagine storiche "suite" (non vendute, accessibili solo da qui)
      "/tasks",
      "/projects",
      "/documents",
      "/knowledge",
      "/people",
      "/departments",
      "/locations",
    ],
    loginPath: "/core/login",
    sessionKey: "core",
    embeds: [],
  },
  construction: {
    key: "construction",
    kind: "sector",
    name: "Mindlead Construction",
    pathPrefixes: [
      "/construction",
      // redirect storici verso Construction
      "/edilizia",
      "/login",
      "/companies",
      "/contacts",
      "/activities",
      "/pipelines",
    ],
    loginPath: "/construction/login",
    sessionKey: "construction",
    sectorModule: "edilizia",
    embeds: [
      { module: "crm", entitlementKeys: CRM_KEYS },
      { module: "billing", entitlementKeys: BILLING_KEYS },
      { module: "supply-buyer", entitlementKeys: ["supply"] },
    ],
    mobileAppId: "com.mindleadsuite.construction",
  },
  supply: {
    key: "supply",
    kind: "sector",
    name: "Mindlead Supply",
    pathPrefixes: ["/supply"],
    loginPath: "/supply/login",
    sessionKey: "supply",
    sectorModule: "supply",
    embeds: [],
  },
  crm: {
    key: "crm",
    kind: "standalone",
    name: "Mindlead CRM",
    pathPrefixes: ["/crm"],
    loginPath: "/crm/login",
    sessionKey: "crm",
    standaloneModule: "crm",
    embeds: [{ module: "crm", entitlementKeys: CRM_KEYS }],
  },
  billing: {
    key: "billing",
    kind: "standalone",
    name: "Mindlead Fatturazione",
    pathPrefixes: ["/billing"],
    loginPath: "/billing/login",
    sessionKey: "billing",
    standaloneModule: "billing",
    embeds: [{ module: "billing", entitlementKeys: BILLING_KEYS }],
  },
};

export const PRODUCT_KEYS = Object.keys(PRODUCTS) as ProductKey[];

export function isProductKey(value: unknown): value is ProductKey {
  return typeof value === "string" && value in PRODUCTS;
}

/** Percorsi condivisi da tutti i prodotti (pubblici o di servizio). */
export const SHARED_PATH_PREFIXES = ["/privacy", "/onboarding", "/auth"] as const;

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Prodotto che possiede un percorso, o null se il percorso è condiviso / sconosciuto. */
export function productForPath(pathname: string): ProductKey | null {
  for (const key of PRODUCT_KEYS) {
    if (PRODUCTS[key].pathPrefixes.some((p) => matchesPrefix(pathname, p))) return key;
  }
  return null;
}

export function isSharedPath(pathname: string): boolean {
  return SHARED_PATH_PREFIXES.some((p) => matchesPrefix(pathname, p));
}

/** Prodotti di settore che sanno ospitare un modulo di feature. */
export function sectorsEmbedding(module: FeatureModuleKey): ProductKey[] {
  return PRODUCT_KEYS.filter(
    (k) => PRODUCTS[k].kind === "sector" && PRODUCTS[k].embeds.some((e) => e.module === module),
  );
}

/** Prodotto standalone che vende da solo un modulo di feature. */
export function standaloneProductFor(module: FeatureModuleKey): ProductKey | null {
  return PRODUCT_KEYS.find((k) => PRODUCTS[k].standaloneModule === module) ?? null;
}

/**
 * Validazione del registro: un prefisso appartiene a un solo prodotto, ogni modulo
 * ospitato ha un prodotto standalone, ogni settore ha il suo modulo di settore.
 * Restituisce l'elenco degli errori (vuoto = registro coerente).
 */
export function validateProductRegistry(products: Record<ProductKey, ProductDefinition> = PRODUCTS): string[] {
  const errors: string[] = [];
  const owner = new Map<string, ProductKey>();
  for (const key of Object.keys(products) as ProductKey[]) {
    const p = products[key];
    if (p.key !== key) errors.push(`${key}: key interna "${p.key}" diversa dalla chiave del registro`);
    for (const prefix of p.pathPrefixes) {
      const prev = owner.get(prefix);
      if (prev) errors.push(`prefisso ${prefix} dichiarato sia da ${prev} sia da ${key}`);
      owner.set(prefix, key);
      if (SHARED_PATH_PREFIXES.some((s) => s === prefix)) errors.push(`${key}: ${prefix} è un percorso condiviso`);
    }
    if (!p.pathPrefixes.some((prefix) => matchesPrefix(p.loginPath, prefix))) {
      errors.push(`${key}: loginPath ${p.loginPath} fuori dai suoi prefissi`);
    }
    if (p.kind === "sector" && !p.sectorModule) errors.push(`${key}: settore senza sectorModule`);
    if (p.kind === "standalone" && !p.standaloneModule) errors.push(`${key}: standalone senza standaloneModule`);
    const seen = new Set<string>();
    for (const e of p.embeds) {
      if (seen.has(e.module)) errors.push(`${key}: modulo ${e.module} ospitato due volte`);
      seen.add(e.module);
    }
  }
  return errors;
}
